import { useEffect, useMemo, useRef, useState } from 'react';
import type { GenerationAsset } from '../../state/types';
import {
  buildMentionCandidates,
  computeMentionChipRanges,
  type MentionActorSource,
  type MentionCandidate,
  type MentionChipRange,
} from '../../state/mentionUtils';
import { createTextareaMirror } from './useMaterialMention';

// actors 省略時の既定値 (useMaterialMention.ts と同じ理由: 毎回新しい配列リテラルを
// 渡すと useMemo の依存が変わり続けるため、安定した参照を1つ共有する)。
const EMPTY_ACTORS: MentionActorSource[] = [];
const EMPTY_CANDIDATES: MentionCandidate[] = [];

export type MentionTokenChip = {
  /** トークン開始位置 + 対象 id。並び替え中の React key として安定させる。 */
  key: string;
  /** textarea の border-box 左上基準・スクロール補正済みの座標・サイズ。 */
  top: number;
  left: number;
  width: number;
  height: number;
  label: string;
  thumbnailUrl: string | null;
  kind: 'material' | 'actor';
  /** ホバープレビュー (MentionChipHoverPreview) 用の素材名/俳優名。無ければ null。 */
  name: string | null;
};

/**
 * chips のうち、textarea の border-box 相対座標 (x, y) を含む最初のチップを返す。
 * 編集中チップのホバープレビュー (MentionTokenChipOverlay) が、`pointer-events:none`
 * のオーバーレイの代わりに textarea 側の mousemove からヒットテストするために使う
 * 純粋関数 (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)。
 * chips は既にスクロール補正済み (useMentionTokenChips が返す座標系) の前提。
 */
export function findChipAtPoint(chips: MentionTokenChip[], x: number, y: number): MentionTokenChip | null {
  for (const chip of chips) {
    if (x >= chip.left && x < chip.left + chip.width && y >= chip.top && y < chip.top + chip.height) {
      return chip;
    }
  }
  return null;
}

type MeasuredChip = MentionTokenChip & { start: number; end: number };

interface UseMentionTokenChipsParams {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  assets: GenerationAsset[];
  actors?: MentionActorSource[];
  /** 記録由来の他シーンの動画フレーム候補 (buildSceneFrameMentionCandidates)。俳優→素材の後ろに続く。 */
  sceneFrameCandidates?: MentionCandidate[];
}

interface UseMentionTokenChipsResult {
  chips: MentionTokenChip[];
}

/**
 * 素材/俳優 @メンショントークン (`@A#3f9c`) の上に「サムネイル+バッジ」チップを重ねて
 * 見せるための座標計算フック (docs/features/material-at-mention-plan.md
 * 「トークンのチップ表示 (オーバーレイ装飾方式)」節)。本文データ (トークン文字列) は
 * 一切変更しない。表示専用の描画情報だけを返す。
 */
export function useMentionTokenChips({
  textareaRef,
  value,
  assets,
  actors = EMPTY_ACTORS,
  sceneFrameCandidates = EMPTY_CANDIDATES,
}: UseMentionTokenChipsParams): UseMentionTokenChipsResult {
  const candidates = useMemo(
    () => buildMentionCandidates(assets, actors, sceneFrameCandidates),
    [assets, actors, sceneFrameCandidates],
  );
  const ranges = useMemo(() => computeMentionChipRanges(value, candidates), [value, candidates]);

  const [measured, setMeasured] = useState<MeasuredChip[]>([]);
  const [scroll, setScroll] = useState({ top: 0, left: 0 });
  const [selection, setSelection] = useState<{ start: number; end: number; focused: boolean }>(
    { start: 0, end: 0, focused: false },
  );
  const [layoutTick, setLayoutTick] = useState(0);

  // textarea への購読 (ResizeObserver・scroll・selectionchange・focus/blur) の張り替え。
  // SceneDetailColumn の視覚編集 textarea は editingField に応じて条件付き描画される
  // ため、ref のアタッチ/デタッチをフック側から直接検知する手段が無い。依存配列なしで
  // 毎レンダー後に「監視中の要素が変わっていないか」を確認し、変わったときだけ
  // 購読を張り替える (同じ要素なら早期returnで再生成しない)。
  const observedElRef = useRef<HTMLTextAreaElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta === observedElRef.current) return;
    cleanupRef.current?.();
    cleanupRef.current = null;
    observedElRef.current = ta;
    if (!ta) return;

    const onScroll = () => setScroll({ top: ta.scrollTop, left: ta.scrollLeft });
    const onSelectionChange = () => {
      if (document.activeElement !== ta) return;
      setSelection({ start: ta.selectionStart, end: ta.selectionEnd, focused: true });
    };
    const onFocus = () => setSelection({ start: ta.selectionStart, end: ta.selectionEnd, focused: true });
    const onBlur = () => setSelection((prev) => ({ ...prev, focused: false }));
    const ro = new ResizeObserver(() => setLayoutTick((t) => t + 1));

    ta.addEventListener('scroll', onScroll);
    ta.addEventListener('focus', onFocus);
    ta.addEventListener('blur', onBlur);
    document.addEventListener('selectionchange', onSelectionChange);
    ro.observe(ta);
    onScroll();
    if (document.activeElement === ta) onFocus();

    cleanupRef.current = () => {
      ta.removeEventListener('scroll', onScroll);
      ta.removeEventListener('focus', onFocus);
      ta.removeEventListener('blur', onBlur);
      document.removeEventListener('selectionchange', onSelectionChange);
      ro.disconnect();
    };
  });
  // アンマウント時の後始末。StrictMode はマウント直後に一度クリーンアップ→再マウント
  // するため、リスナー解除だけでなく observedElRef / cleanupRef も初期化して
  // 「同じ要素なので早期return」の誤判定で再購読が永久にスキップされるのを防ぐ。
  useEffect(() => () => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    observedElRef.current = null;
  }, []);

  // コンテナ幅の変化 (window resize によるレイアウト変化) でも折り返し位置が変わるため
  // 再計測する。
  useEffect(() => {
    const onResize = () => setLayoutTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 計測: ranges か textarea のレイアウト (layoutTick) が変わったら、1回のミラーパスで
  // 全トークンをまとめて計測する。
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta || ranges.length === 0) {
      setMeasured((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const style = window.getComputedStyle(ta);
    // ミラーの offsetTop/Left は content/padding 基準なので、textarea の border-box
    // 基準へ補正するため border 幅を加算する。
    const borderTop = parseFloat(style.borderTopWidth) || 0;
    const borderLeft = parseFloat(style.borderLeftWidth) || 0;
    const mirror = createTextareaMirror(ta);

    // ミラーへ「トークン前のテキストノード + トークンを包む span」を順に流し込む。
    let cursor = 0;
    const spans: { span: HTMLSpanElement; range: MentionChipRange }[] = [];
    for (const range of ranges) {
      if (range.start > cursor) {
        mirror.appendChild(document.createTextNode(value.slice(cursor, range.start)));
      }
      const span = document.createElement('span');
      span.textContent = value.slice(range.start, range.end);
      mirror.appendChild(span);
      spans.push({ span, range });
      cursor = range.end;
    }

    const next: MeasuredChip[] = [];
    for (const { span, range } of spans) {
      // 行折り返しでトークンが分割された場合はチップ化をスキップする (生テキスト表示のまま)。
      if (span.getClientRects().length > 1) continue;
      next.push({
        key: `${range.start}-${range.candidate.target.id}`,
        top: span.offsetTop + borderTop,
        left: span.offsetLeft + borderLeft,
        width: span.offsetWidth,
        height: span.offsetHeight,
        label: range.label,
        thumbnailUrl: range.candidate.target.thumbnailUrl ?? null,
        kind: range.candidate.kind === 'actor' ? 'actor' : 'material',
        name: range.candidate.target.name ?? null,
        start: range.start,
        end: range.end,
      });
    }
    document.body.removeChild(mirror);
    setMeasured(next);
  }, [ranges, value, layoutTick]);

  const chips = useMemo<MentionTokenChip[]>(() => (
    measured
      // 編集中の生テキスト表示: フォーカス中に選択範囲 (キャレット含む) がトークン範囲と
      // 交差するものは隠す。クリックや Backspace で自然に生テキスト編集へ戻れるようにするため。
      .filter((chip) => !selection.focused || !(selection.start <= chip.end && selection.end >= chip.start))
      .map((chip) => ({
        key: chip.key,
        top: chip.top - scroll.top,
        left: chip.left - scroll.left,
        width: chip.width,
        height: chip.height,
        label: chip.label,
        thumbnailUrl: chip.thumbnailUrl,
        kind: chip.kind,
        name: chip.name,
      }))
  ), [measured, selection, scroll]);

  return { chips };
}
